import React, { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { motion } from 'framer-motion'

import Navbar from '../../components/navbar/navbar'
import FormTwo from '../../components/form-two'
import ScrollToTop from '../../components/scroll-to-top'
import HeroSlider from '../../components/hero-slider'
import api from '../../utils/api'


// Lazy loaded components for below-the-fold content
const ExplorePropertyOne = React.lazy(() => import('../../components/explore-property-one'))
const PropertySlider = React.lazy(() => import('../../components/property-slider'))
const SellPropertyOne = React.lazy(() => import('../../components/sell-property-one'))
const TeamOne = React.lazy(() => import('../../components/team-one'))
const ClientOne = React.lazy(() => import('../../components/client-one'))
const BlogOne = React.lazy(() => import('../../components/blog-one'))
const BrokerInsights = React.lazy(() => import('../../components/broker-insights'))
const CategoryPropertySearch = React.lazy(() => import('../../components/category-property-search'))
const NewlyLaunchedProjects = React.lazy(() => import('../../components/newly-launched-projects'))
const HomeHelpfulTools = React.lazy(() => import('../../components/home-helpful-tools'))
const RealEstateDevelopers = React.lazy(() => import('../../components/real-estate-developers'))
const ReelsSection = React.lazy(() => import('../../components/reels-section'))
const ExpertPodcasts = React.lazy(() => import('../../components/expert-podcasts'))
const FeaturedPropertySlider = React.lazy(() => import('../../components/featured-property-slider'))
const WhyChooseUs = React.lazy(() => import('../../components/why-choose-us'))
const StatsCounter = React.lazy(() => import('../../components/stats-counter'))
const ExploreByLocation = React.lazy(() => import('../../components/explore-by-location'))
const RecentProjectSlider = React.lazy(() => import('../../components/recent-project-slider'))
const Footer = React.lazy(() => import('../../components/footer'))

const fadeInVariant = {
    hidden: { opacity: 0, y: 30 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.6 } }
};

const staggerContainer = {
    hidden: { opacity: 0 },
    visible: {
        opacity: 1,
        transition: {
            staggerChildren: 0.2
        }
    }
};

export default function IndexNine() {
    const [homeSections, setHomeSections] = useState({
        featuredProperties: true,
        newlyLaunched: true,
        categorySearch: true
    });

    useEffect(() => {
        api.getSystemSettings().then(data => {
            if (data?.homeSections) {
                setHomeSections({
                    featuredProperties: data.homeSections.featuredProperties !== false,
                    newlyLaunched: data.homeSections.newlyLaunched !== false,
                    categorySearch: data.homeSections.categorySearch !== false,
                });
            }
        }).catch(console.error);
    }, []);

    return (
        <>
            <Navbar transparent={false} />

            <HeroSlider />

            <div className="bg-white relative flex flex-col">
                <div className="relative z-20 focus-within:z-50">
                <div className="standard-container transform -translate-y-12 md:-translate-y-[80px] -mb-12 md:-mb-[80px]">
                    <div className="flex flex-wrap justify-center md:-mx-4">
                        <div className="lg:w-11/12 md:w-full md:px-4">
                            <motion.div
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.8, delay: 0.3 }}
                            >
                                <FormTwo />
                            </motion.div>
                        </div>
                    </div>
                </div>
            </div>

            <React.Suspense fallback={<div className="py-5 text-center"><div className="spinner-border text-primary"></div></div>}>

                {/* Featured Properties Section */}
                {homeSections.featuredProperties && (
                    <motion.section
                        className="relative z-10 bg-white pb-6 md:pb-8 pt-8 md:pt-12 mt-0 md:mt-0"
                        initial="hidden"
                        whileInView="visible"
                        viewport={{ once: true, margin: "-100px" }}
                        variants={fadeInVariant}
                    >


                        <div className="standard-container relative z-10">
                            <div className="flex justify-center -mx-4">
                                <div className="lg:w-7/12 md:w-10/12 text-center px-4">
                                    <div className="sec-heading text-center mb-2 md:mb-6">
                                        <div className="hidden md:flex items-center justify-center gap-3 md:gap-5 mb-2">
                                            <i className="fa-solid fa-fire-flame-curved text-accent/60 text-[12px] md:text-[14px]"></i>
                                            <span className="text-accent font-extrabold text-[14px] md:text-[18px] uppercase tracking-[0.3em] block animate-pulse drop-shadow-sm">Exclusive Deals</span>
                                            <i className="fa-solid fa-fire-flame-curved text-accent/60 text-[12px] md:text-[14px]"></i>
                                        </div>
                                        <h2 className="font-black text-[22px] md:text-[32px] uppercase tracking-wide drop-shadow-sm leading-tight whitespace-nowrap overflow-hidden text-ellipsis mb-2 bg-clip-text text-transparent bg-gradient-to-r from-[#05144b] to-[#4c1d95]">
                                            Featured Properties
                                        </h2>
                                        <div className="w-[60px] md:w-[120px] h-1.5 bg-accent mx-auto rounded-full"></div>
                                    </div>
                                </div>
                            </div>
                            <FeaturedPropertySlider />

                            <div className="mt-4 md:mt-6 text-center px-4">
                                <Link
                                    to="/project-list?isOurPick=true"
                                    className="inline-flex bg-gradient-to-r from-[#05144b] to-[#4c1d95] text-white px-4 sm:px-5 md:px-6 py-3 md:py-3.5 rounded-xl font-extrabold text-xs sm:text-sm hover:opacity-90 transition-all shadow-xl shadow-indigo-950/20 items-center gap-2 sm:gap-3 group uppercase tracking-wider whitespace-nowrap"
                                >
                                    View All Featured Projects
                                    <i className="fa-solid fa-arrow-right text-xs group-hover:translate-x-2 transition-transform"></i>
                                </Link>
                            </div>
                        </div>
                    </motion.section>
                )}

                <motion.div
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true, margin: "-100px" }}
                    variants={fadeInVariant}
                >
                    <WhyChooseUs />
                </motion.div>




                {/* Newly Launched Projects Section */}
                {homeSections.newlyLaunched && (
                    <motion.div
                        initial="hidden"
                        whileInView="visible"
                        viewport={{ once: true, margin: "-100px" }}
                        variants={fadeInVariant}
                    >
                        <NewlyLaunchedProjects />
                    </motion.div>
                )}

                <HomeHelpfulTools />

                {/* What Are You Looking For Section */}
                {homeSections.categorySearch && (
                    <motion.div
                        initial="hidden"
                        whileInView="visible"
                        viewport={{ once: true, margin: "-100px" }}
                        variants={fadeInVariant}
                    >
                        <CategoryPropertySearch />
                    </motion.div>
                )}

                <ExploreByLocation />



                <motion.section
                    className="bg-white border-y border-gray-100 pt-6 pb-6 md:pt-10 md:pb-12 overflow-hidden relative"
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true, margin: "-100px" }}
                    variants={fadeInVariant}
                >
                    <div className="standard-container relative z-10">
                        <div className="flex flex-col items-center justify-center mb-8 md:mb-12 text-center px-4">
                            <div className="flex items-center justify-center gap-3 md:gap-5 mb-2">
                                <i className="fa-solid fa-video text-accent/60 text-[12px] md:text-[14px]"></i>
                                <span className="text-accent font-extrabold text-[14px] md:text-[18px] uppercase tracking-[0.3em] block animate-pulse drop-shadow-sm">Visual Tours</span>
                                <i className="fa-solid fa-video text-accent/60 text-[12px] md:text-[14px]"></i>
                            </div>
                            <div className="flex items-center justify-center gap-3">
                                <h2 className="font-black text-[24px] md:text-[38px] mb-1 uppercase tracking-wide drop-shadow-sm bg-clip-text text-transparent bg-gradient-to-r from-[#05144b] to-[#4c1d95]">
                                    LATEST PROPERTY REELS
                                </h2>
                                <span className="bg-red-500 text-white text-[9px] font-black rounded-full px-2 py-1 animate-pulse uppercase tracking-widest leading-none shrink-0 mb-2">LIVE</span>
                            </div>
                            <div className="w-[80px] md:w-[120px] h-1.5 bg-[#f1ae24] mx-auto rounded-full mt-3 mb-4"></div>
                            <p className="text-black text-sm md:text-lg font-medium max-w-2xl leading-relaxed italic">Stay engaged with our latest video tours and project highlights.</p>
                        </div>
                        <ReelsSection />
                    </div>
                </motion.section>

                <ExpertPodcasts />

                <motion.div
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true, margin: "-100px" }}
                    variants={fadeInVariant}
                >
                    <RealEstateDevelopers />
                </motion.div>

                <motion.section
                    className="bg-white pt-0 pb-4"
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true, margin: "-100px" }}
                    variants={fadeInVariant}
                >
                    <div className="standard-container">
                        <div className="flex justify-center mb-6">
                            <div className="lg:w-10/12 text-center">
                                <div className="flex flex-col items-center gap-2">
                                    <div className="flex items-center gap-4 mb-2">
                                        <div className="w-10 h-[2px] bg-accent"></div>
                                        <span className="text-accent font-black text-[10px] uppercase tracking-[0.4em]">Our Journal</span>
                                        <div className="w-10 h-[2px] bg-accent"></div>
                                    </div>
                                    <h2 className="font-black text-[18px] md:text-[38px] uppercase tracking-wide drop-shadow-sm mb-2 whitespace-nowrap overflow-hidden px-1 bg-clip-text text-transparent bg-gradient-to-r from-[#05144b] to-[#4c1d95]">Latest <span className="text-accent italic">News</span> &amp; Articles</h2>
                                    <div className="flex flex-col md:flex-row items-center justify-center gap-6 text-center">
                                        <p className="text-black text-lg font-medium max-w-2xl leading-relaxed mb-0">Stay updated with the latest trends, tips, and insights from the world of real estate and property management.</p>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <BlogOne />
                        <div className="mt-12 text-center px-4">
                            <Link
                                to="/blog"
                                className="inline-flex bg-gradient-to-r from-[#4c1d95] to-[#7c3aed] text-white px-10 py-3 rounded-xl font-bold text-sm hover:opacity-90 transition-all shadow-xl shadow-purple-900/20 items-center gap-3 group"
                            >
                                VIEW ALL ARTICLES
                                <i className="fa-solid fa-arrow-right text-xs group-hover:translate-x-2 transition-transform"></i>
                            </Link>
                        </div>
                    </div>
                </motion.section>

                <motion.section
                    className="bg-white pt-0 pb-4"
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true, margin: "-100px" }}
                    variants={fadeInVariant}
                >
                    <div className="standard-container">
                        <div className="flex justify-center mb-6">
                            <div className="lg:w-10/12 text-center">
                                <div className="flex flex-col items-center gap-2">
                                    <div className="flex items-center gap-4 mb-2">
                                        <div className="w-10 h-[2px] bg-accent"></div>
                                        <span className="text-accent font-black text-[10px] uppercase tracking-[0.4em]">Testimonials</span>
                                        <div className="w-10 h-[2px] bg-accent"></div>
                                    </div>
                                    <h2 className="font-black text-[28px] md:text-[38px] uppercase tracking-wide drop-shadow-sm mb-2 bg-clip-text text-transparent bg-gradient-to-r from-[#05144b] to-[#4c1d95]">Good <span className="text-accent italic">Reviews</span> by Customers</h2>
                                    <p className="text-black text-lg font-medium max-w-3xl mx-auto leading-relaxed">Read what our satisfied clients have to say about their experiences working with our real estate experts.</p>
                                </div>
                            </div>
                        </div>
                        <ClientOne />
                    </div>
                </motion.section>

                <motion.div
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true, margin: "-100px" }}
                    variants={fadeInVariant}
                >
                    <BrokerInsights />
                </motion.div>




                <div className="relative z-50 -mb-20 md:-mb-28 lg:-mb-36">
                    <StatsCounter />
                </div>
                <Footer />

                <ScrollToTop />
            </React.Suspense>
            </div>
        </>
    )
}
